import { z } from 'zod';

const production = process.env.NODE_ENV === 'production';

const AppConfigSchema = z
  .object({
    name: z
      .string({
        description: `This is the name of your SaaS. Ex. "SupplyAutomate"`,
        required_error: `Please provide the variable NEXT_PUBLIC_PRODUCT_NAME`,
      })
      .min(1),
    title: z
      .string({
        description: `This is the default title tag of your SaaS.`,
        required_error: `Please provide the variable NEXT_PUBLIC_SITE_TITLE`,
      })
      .min(1),
    description: z.string({
      description: `This is the default description of your SaaS.`,
      required_error: `Please provide the variable NEXT_PUBLIC_SITE_DESCRIPTION`,
    }),
    url: z
      .string({
        required_error: `Please provide the variable NEXT_PUBLIC_SITE_URL`,
      })
      .url({
        message: `You are deploying a production build but have entered a NEXT_PUBLIC_SITE_URL variable using http instead of https. It is very likely that you have set the incorrect URL. The build will now fail to prevent you from from deploying a faulty configuration. Please provide the variable NEXT_PUBLIC_SITE_URL with a valid URL, such as: 'https://example.com'`,
      }),
    locale: z
      .string({
        description: `This is the default locale of your SaaS.`,
        required_error: `Please provide the variable NEXT_PUBLIC_DEFAULT_LOCALE`,
      })
      .default('en'),
    theme: z.enum(['light', 'dark', 'system']),
    production: z.boolean(),
    themeColor: z.string(),
    themeColorDark: z.string(),
  })
  .refine(
    (schema) => {
      const isCI = process.env.NEXT_PUBLIC_CI;

      if (isCI ?? !schema.production) {
        return true;
      }

      return !schema.url.startsWith('http:');
    },
    {
      message: `Please provide a valid HTTPS URL. Set the variable NEXT_PUBLIC_SITE_URL with a valid URL, such as: 'https://example.com'`,
      path: ['url'],
    },
  )
  .refine(
    (schema) => {
      return schema.themeColor !== schema.themeColorDark;
    },
    {
      message: `Please provide different theme colors for light and dark themes.`,
      path: ['themeColor'],
    },
  );

const rawConfig = {
  name: process.env.NEXT_PUBLIC_PRODUCT_NAME ?? 'SupplyAutomate',
  title:
    process.env.NEXT_PUBLIC_SITE_TITLE ??
    'SupplyAutomate | Modern Supply Chain Management',
  description:
    process.env.NEXT_PUBLIC_SITE_DESCRIPTION ??
    'Your entire supply chain in one simple interface. Manage suppliers, track production, and calculate exact landed costs.',
  url: process.env.NEXT_PUBLIC_SITE_URL,
  locale: process.env.NEXT_PUBLIC_DEFAULT_LOCALE ?? 'en',
  theme: process.env.NEXT_PUBLIC_DEFAULT_THEME_MODE ?? 'system',
  themeColor: process.env.NEXT_PUBLIC_THEME_COLOR ?? '#136dec',
  themeColorDark: process.env.NEXT_PUBLIC_THEME_COLOR_DARK ?? '#101822',
  production,
};

// Log which variables are missing to make Vercel build debugging easier
if (!rawConfig.url) {
  console.error(
    '\n[app.config] ERROR: NEXT_PUBLIC_SITE_URL is not set.\n' +
      'Set it in Vercel > Settings > Environment Variables.\n' +
      'Example: https://your-domain.vercel.app\n',
  );
}

const result = AppConfigSchema.safeParse(rawConfig);

if (!result.success) {
  console.error(
    '\n[app.config] Validation failed. Issues:\n',
    JSON.stringify(result.error.issues, null, 2),
    '\n\nRaw values (url redacted):',
    JSON.stringify(
      { ...rawConfig, url: rawConfig.url ? '(set)' : '(MISSING)' },
      null,
      2,
    ),
    '\n',
  );

  throw result.error;
}

const appConfig = result.data;

export default appConfig;
